// assets/pawbau-configurator.js
import { Component } from '@theme/component';

/**
 * @typedef {object} PawbauRefs
 * @property {HTMLElement} root - Wrapper holding the settings as data attributes.
 * @property {HTMLScriptElement} catalogue - What the conversation can put in the cart.
 * @property {HTMLButtonElement} openButton - Opens the panel from the page.
 * @property {HTMLElement} dock - The same button, waiting at the bottom of the screen.
 * @property {HTMLButtonElement} dockButton - The button inside it.
 * @property {HTMLDialogElement} panel - The conversation itself.
 * @property {HTMLButtonElement} closeButton - Closes the panel.
 * @property {HTMLElement} recap - The pieces made in this session.
 * @property {HTMLElement} thread - The bubbles.
 * @property {HTMLTemplateElement} avatarTemplate - Mia's face, cloned per message.
 * @property {HTMLElement} replyPet - Dog or cat.
 * @property {HTMLElement} replyBreed - Breed search and shortcuts.
 * @property {HTMLElement} replyName - Name field.
 * @property {HTMLElement} replyProduct - The three pieces.
 * @property {HTMLElement} replyOptions - Colour, and cord when there is one.
 * @property {HTMLElement} replyStones - The months.
 * @property {HTMLElement} replyGift - Gift box or not.
 * @property {HTMLElement} replyDone - What now.
 * @property {HTMLInputElement} breedInput - Breed search field.
 * @property {HTMLUListElement} breedList - Suggestions.
 * @property {HTMLElement} popular - Most chosen breeds.
 * @property {HTMLElement} popularHint - Heading above them.
 * @property {HTMLInputElement} nameInput - Pet name field.
 * @property {HTMLInputElement} haloBox - The halo, for a pet who is no longer here.
 * @property {HTMLElement} haloArt - Their own breed wearing the halo, so the offer explains itself.
 * @property {HTMLButtonElement} nameGo - Confirms the name.
 * @property {HTMLElement} carousel - The pieces to choose from.
 * @property {HTMLElement} choices - Colour and cord buttons.
 * @property {HTMLElement} months - The twelve stones.
 * @property {HTMLButtonElement} stonesSkip - No stones.
 * @property {HTMLButtonElement} stonesGo - Confirm the chosen stones.
 * @property {HTMLButtonElement} giftYes - With gift box.
 * @property {HTMLButtonElement} giftNo - Without.
 * @property {HTMLButtonElement} againProduct - Same design, another piece.
 * @property {HTMLButtonElement} againDesign - Start a new design.
 * @property {HTMLButtonElement} goCart - Leave for the cart.
 * @property {HTMLElement} status - Short message under the composer.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const MAX_SUGGESTIONS = 8;
// How Mia writes: she reads what you said, then types at a fast but human pace.
const READING_MS = [180, 420];
const PER_CHAR_MS = 22;
const TYPING_MIN = 420;
const TYPING_MAX = 1800;
// How long each line of the bench work stays up while the piece goes into the cart.
const PACKING_STEP_MS = 1600;
const STEPS = ['pet', 'breed', 'name', 'product', 'options', 'stones', 'gift', 'done'];
/** The finishes, so a colour name can be shown as a colour. */
const FINISHES = { Silver: '#C8CBCE', Gold: '#CDA35D', 'Rose Gold': '#E0B2A4', Black: '#2E2E30' };
const CORDS = { Black: '#20201F', Ivory: '#EFE7D6', 'Army Green': '#4B5320', Burgundy: '#6E1A2B' };

/** How each piece is drawn in the 200x120 preview: its chain, where the charm hangs, how the stones sit on it. */
/** The finishes as drawn: a shade deeper than the swatch, or silver disappears on a pale panel. */
const DRAWN = { Silver: '#8E959B', Gold: '#C39A52', 'Rose Gold': '#CE9486', Black: '#1C1C1E' };

/** A colour pushed toward black or white: the drawn braid needs its own shadows and lights. */
function mix(hex, to, t) {
  const a = parseInt(hex.slice(1), 16);
  const b = parseInt(to.slice(1), 16);
  const c = (shift) => Math.round(((a >> shift) & 255) * (1 - t) + ((b >> shift) & 255) * t);
  return `rgb(${c(16)} ${c(8)} ${c(0)})`;
}

/** One SVG element with its attributes, appended to its parent. */
function draw(name, attrs, parent) {
  const node = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
  parent?.append(node);
  return node;
}

/**
 * La corda intrecciata, disegnata al posto delle foto: un punto dopo l'altro attraverso la fascia,
 * con il nodo che sporge una volta sopra e una sotto. Ogni colore e' lo stesso disegno, cambia la tinta.
 */
function braid(parent, x0, x1, cy, colour, h = 18) {
  const group = draw('g', {}, parent);
  const edge = mix(colour, '#000000', 0.5);
  const shade = mix(colour, '#000000', 0.18);
  const light = mix(colour, '#ffffff', 0.4);
  const n = Math.max(2, Math.round((x1 - x0) / (h * 0.42)));
  const step = (x1 - x0) / n;
  const top = cy - h / 2;
  draw('rect', { x: x0, y: top + 1, width: x1 - x0, height: h - 2, rx: 2, fill: edge }, group);
  for (let i = 0; i < n; i++) {
    const x = x0 + step * i;
    const up = i % 2 === 0;
    draw('ellipse', { cx: x + step / 2, cy: up ? top + 0.5 : top + h - 0.5, rx: step * 0.55, ry: h * 0.16, fill: shade, stroke: edge, 'stroke-width': 0.7 }, group);
    draw('rect', { x: x + step * 0.06, y: top, width: step * 0.88, height: h, rx: step * 0.44, fill: colour, stroke: edge, 'stroke-width': 0.7 }, group);
    const [y1, y2] = up ? [top + h * 0.2, top + h * 0.8] : [top + h * 0.8, top + h * 0.2];
    draw('line', { x1: x + step * 0.25, y1, x2: x + step * 0.75, y2, stroke: light, 'stroke-width': 0.8, 'stroke-linecap': 'round', opacity: 0.7 }, group);
  }
  return group;
}

const HOLDERS = {
  necklace: {
    line: 'M18 10 C 30 52, 62 66, 100 66 C 138 66, 170 52, 182 10',
    thickness: 2, charm: 52, gap: 4, bead: 4.4, first: 27, step: 12, beads: 64,
  },
  bracelet: {
    line: 'M12 46 C 36 82, 164 82, 188 46',
    thickness: 5, charm: 42, gap: 5, bead: 5.6, first: 24, step: 14, beads: 78,
    // il ciondolo del bracciale non pende: sta nel cinturino, tenuto dai due anelli laterali,
    // che stanno al 44 % dell'altezza contando dal basso — cioe al 56 % contando dall'alto
    sides: true,
    seat: 0.56,
  },
  keychain: {
    line: 'M100 14 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0',
    thickness: 2.2, charm: 62, gap: 3, bead: 4,
    // a short chain runs from the ring down to the charm, and the stones hang off that chain,
    // one side then the other, the way the keychain is made
    chain: { x: 100, top: 24, bottom: 62 },
  },
};

/** Every stone the shop sells: what it is said to carry, and the colour to draw it. */
const STONES = {
  Garnet: ['Protection', '#7B1E28'],
  Amethyst: ['Peace', '#7B4EA8'],
  'Light Amethyst': ['Serenity', '#C3A6DE'],
  Aquamarine: ['Courage', '#7FC4D6'],
  Diamond: ['Clarity', '#E4E6EA'],
  'Crystal Quartz': ['Clarity', '#ECEBEF'],
  Emerald: ['Growth', '#2E8B57'],
  Moonstone: ['Calm', '#EFE8DC'],
  Ruby: ['Passion', '#C41E3A'],
  'Red Onyx': ['Strength', '#8B2B24'],
  Peridot: ['Joy', '#9DC209'],
  Sapphire: ['Wisdom', '#0F52BA'],
  'Pink Tourmaline': ['Hope', '#F0A0B4'],
  Topaz: ['Warmth', '#F0B458'],
  Citrine: ['Abundance', '#E3A81E'],
  Tanzanite: ['Wonder', '#5B4FC7'],
  Turquoise: ['Luck', '#3FC9C0'],
};

/** What a piece is called in the chat: the keychain also hangs on a bag, so it says so. */
function wareName(ware, short = false) {
  if (ware.key === 'keychain') return short ? 'Keychain / Bag\u00a0charm' : String(ware.title || '').replace(/Keychain/, 'Keychain / Bag Charm');
  if (short && ware.key) return ware.key[0].toUpperCase() + ware.key.slice(1);
  return ware.title;
}

/** "January - Garnet" is one variant: the month names it, the stone gives it meaning. */
function readStone(value) {
  const [month, stone = ''] = String(value).split(/\s*[-–—]\s*/);
  const [meaning = '', colour = '#CCC'] = STONES[stone.trim()] || [];
  return { month: month.trim(), stone: stone.trim(), meaning, colour };
}

/**
 * Mia's atelier: a conversation that designs a charm, chooses what to wear it on, and fills the cart.
 *
 * The cutting file is prepared before the charm goes in the cart, so its link travels with the line
 * item and can be read in the admin without opening anything else.
 *
 * @extends Component<PawbauRefs>
 */
class PawbauConfigurator extends Component {
  requiredRefs = ['root', 'openButton', 'panel', 'thread', 'composer', 'breedInput', 'nameInput', 'status'];

  /** @type {string[]} */
  #breeds = [];
  /** @type {Set<string>} */
  #cats = new Set();
  /** @type {Set<string>} */
  #horses = new Set();
  /** @type {'dog' | 'cat' | 'horse' | null} */
  #species = null;
  /** @type {string | null} */
  #breed = null;
  /** @type {{products: object[], gift: object}} Each piece carries its own birthstones. */
  #catalogue = { products: [], gift: null };
  /** @type {object | null} The piece being configured. */
  #ware = null;
  /** @type {Record<string, string>} Chosen option values, by option name. */
  #picked = {};
  /** @type {string[]} Chosen months, in the order they were tapped. */
  #stones = [];
  /** @type {string} */
  #step = 'pet';
  /** The charm Mia has already drawn for the current breed and name. */
  #drawn = false;
  /** @type {string} The prepared cutting file for this exact charm. */
  #dxf = '';
  /** @type {string} The same charm as a picture, for the cart to show. */
  #picture = '';
  #painted = '';
  /** @type {string} Which piece the prepared file was cut for: the rings go elsewhere on a bracelet. */
  #cut = '';
  /** @type {boolean} A halo above the head: asked with the name, cut into every piece for that pet. */
  #halo = false;
  /** @type {{key: string, files: Promise<{url: string, image: string}>} | null} The file started while the
   *  stones and the gift box are asked: by the time they press the last button it is usually ready. */
  #early = null;
  #started = false;
  /** Mia writes one line at a time; going back cancels what she had not said yet. */
  #queue = Promise.resolve();
  #turn = 0;
  /** @type {Map<string, {viewBox: string, path: string}>} */
  #shapeCache = new Map();
  /** @type {Map<string, object>} The same silhouettes, wearing the halo: only the card uses them. */
  #haloCache = new Map();

  /** @type {HTMLImageElement | null} The gift box photo, fetched before Mia sends it. */
  #giftPhoto = null;
  /** @type {{viewBox: string, path: string} | null} The charm as drawn, kept for the stones preview. */
  #charm = null;
  /** @type {HTMLElement | null} The bubble it was sent in, so stones can appear beside it. */
  #charmBubble = null;
  /** @type {object[]} Everything designed in this session, newest last. */
  #made = [];
  /** @type {{order: string, t: string, box: boolean} | null} Opened from the Track page to add a pet to a paid order:
   *  the piece goes back there instead of into the cart, at the extra-piece price. Only for this page view. */
  #adding = null;
  /** @type {number} Which of them is being reworked, or -1. */
  #editing = -1;
  /** Following the conversation, until the buyer scrolls up. */
  #pinned = true;
  #highlighted = -1;
  /** @type {Promise<void>} The breed list, which a link back from an email has to wait for. */
  #breedsReady = Promise.resolve();
  /** @type {Set<string>} Charms already told to Klaviyo: drawing the same one again is not a new event. */
  #tracked = new Set();

  connectedCallback() {
    super.connectedCallback();

    // The settings live on the wrapper inside this element, not on the element itself.
    this.settings = this.refs.root.dataset;
    this.engine = (this.settings.engine || '').replace(/\/+$/, '');
    this.#readCatalogue();
    this.#breedsReady = this.#loadBreeds();

    const r = this.refs;
    r.openButton.addEventListener('click', () => this.#open());
    r.dockButton?.addEventListener('click', () => this.#open());
    // any link to #design on the page, from the sections below, opens the chat
    // the visitor got the Studio in the A/B test: links and email links are its to open
    const studio = document.documentElement.dataset.customizer === 'studio';
    document.addEventListener('click', (event) => {
      if (studio) return;
      const link = event.target instanceof Element ? event.target.closest('a[href$="#design"]') : null;
      if (!link || new URL(link.href, location.href).pathname !== location.pathname) return;
      event.preventDefault();
      this.#open();
    });
    // a link back from an email carries the pet: the conversation picks up at the drawing
    const back = new URLSearchParams(location.search);
    // from the Track page (?pb_add=44702&pb_t=<code>): a pet for an order already paid, so always the chat
    if (back.get('pb_add') && back.get('pb_t')) {
      this.#adding = { order: back.get('pb_add'), t: back.get('pb_t'), box: back.get('pb_box') === '1' };
      history.replaceState(null, '', location.pathname);
      this.#open();
    } else if (!studio && back.get('pet') && back.get('breed')) this.#resume(back.get('breed'), back.get('pet'), back.get('halo') === '1');
    else if (!studio && location.hash === '#design') this.#open();
    r.closeButton?.addEventListener('click', () => r.panel.close());
    r.replyPet?.addEventListener('click', this.#onSpeciesClick);
    r.breedInput.addEventListener('input', this.#onBreedInput);
    r.breedInput.addEventListener('keydown', this.#onBreedKeys);
    r.nameInput.addEventListener('input', this.#onNameInput);
    r.haloBox?.addEventListener('change', this.#onHaloChange);
    r.nameInput.addEventListener('keydown', this.#onNameKeys);
    r.pairGo?.addEventListener('click', this.#confirmPair);
    r.nameGo?.addEventListener('click', () => this.#confirmName());
    r.stonesSkip?.addEventListener('click', () => this.#confirmStones(true));
    r.stonesGo?.addEventListener('click', () => this.#confirmStones(false));
    r.giftYes?.addEventListener('click', () => this.#finish(true));
    r.giftNo?.addEventListener('click', () => this.#finish(false));
    r.againProduct?.addEventListener('click', () => this.#againOnAnotherPiece());
    r.againDesign?.addEventListener('click', () => this.#restart());
    r.goCart?.addEventListener('click', () => {
      window.location.href = this.settings.cartUrl || '/cart';
    });
    r.thread.addEventListener('click', this.#onChangeClick);

    this.#watchScrolling();
    this.#watchTheButton();
  }

  #readCatalogue() {
    try {
      this.#catalogue = JSON.parse(this.refs.catalogue?.textContent || '{}');
    } catch {
      this.#warn('The pieces are not set up yet.');
    }
  }

  async #loadBreeds() {
    try {
      const res = await fetch(`${this.engine}/breeds`);
      const data = await res.json();
      const arrivate = Array.isArray(data.breeds) ? data.breeds : [];
      this.#breeds = arrivate.filter((breed) => !PawbauConfigurator.DOPPIONI[breed]);
      this.#cats = new Set(Array.isArray(data.cats) ? data.cats : []);
      this.#horses = new Set(Array.isArray(data.horses) ? data.horses : []);
      if (this.#species) this.#fillPopular(); // the list can land after the first answer
    } catch {
      this.#warn('The breed list is not reachable right now.');
    }
  }

  /**
   * Due disegni identici non meritano due voci: Tabby e Short-Hair-Cat sono lo stesso
   * tracciato, quindi ne teniamo una sola e le diamo il nome con cui la cercano tutti e due.
   */
  static DOPPIONI = { Tabby: 'Short-Hair-Cat' };
  static NOMI = { 'Short-Hair-Cat': 'Short Hair Cat/Tabby' };

  /** "Border-Collie" reads as "Border Collie" everywhere the buyer looks. */
  #label(breed) {
    const scelto = PawbauConfigurator.NOMI[breed];
    if (scelto) return scelto;
    // le varianti di disegno sono affari nostri: il cliente legge il nome della razza,
    // e le parole scritte minuscole nel catalogo (cat, horse) si leggono meglio con l'iniziale grande
    return breed
      .replace(/-\(Version-[A-Z]\)$/, '')
      .replace(/-/g, ' ')
      .replace(/\b[a-z]+/g, (parola) => parola[0].toUpperCase() + parola.slice(1));
  }

  #money(cents) {
    const format = this.settings.moneyFormat || '${{amount}}';
    const amount = (Number(cents) / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  /** Which animal a breed belongs to: anything that is not a cat or a horse is a dog. */
  #speciesOf(breed) {
    if (this.#cats.has(breed)) return 'cat';
    if (this.#horses.has(breed)) return 'horse';
    return 'dog';
  }

  #ofSpecies() {
    if (!this.#species) return this.#breeds;
    return this.#breeds.filter((breed) => this.#speciesOf(breed) === this.#species);
  }

  #speciesLabel(species) {
    return {
      cat: this.settings.catLabel,
      horse: this.settings.horseLabel,
      dog: this.settings.dogLabel,
    }[species] || '';
  }

  // — opening and closing ———————————————————————————————————————

  #open() {
    window.pbEv?.('open');
    const { panel, dock } = this.refs;
    if (dock) dock.hidden = true;
    if (typeof panel.showModal === 'function') panel.showModal();
    else panel.setAttribute('open', '');

    if (!this.#started) {
      this.#started = true;
      // back from the Track page: one welcome-back line that says it all, instead of the first-visit hello
      if (this.#adding) {
        this.#mia(this.#addingLine(), 0, true);
      } else {
        this.#mia(this.settings.welcome || '', 0, true);
        this.#sayTip(0);
      }
      this.#goto('pet');
    }
  }

  /** Back from an email: the same pet, drawn again, and the conversation carries on from the piece. */
  async #resume(breed, name, halo) {
    // the pet's name leaves the address bar once it has been read
    history.replaceState(null, '', location.pathname + location.hash);
    await this.#breedsReady;
    const known = this.#breeds.includes(breed) ? breed : PawbauConfigurator.DOPPIONI[breed];
    if (!known || this.#started) return this.#open();

    this.#started = true;
    const { panel, dock } = this.refs;
    if (dock) dock.hidden = true;
    if (typeof panel.showModal === 'function') panel.showModal();
    else panel.setAttribute('open', '');

    this.#breed = known;
    this.#species = this.#speciesOf(known);
    this.#halo = halo;
    if (this.refs.haloBox) this.refs.haloBox.checked = halo;
    this.refs.breedInput.value = this.#label(known);
    this.refs.nameInput.value = name;
    const pet = this.#petName();

    // without a name there is nobody to say it of: the usual welcome
    const hello = pet ? (this.settings.welcomeBack || '').replace(/\[name\]/g, pet) : '';
    this.#mia(hello || this.settings.welcome || '', 0, true);
    // her line first, then the two answers they gave last time, then the drawing
    this.#queue = this.#queue.then(() => {
      this.#you(this.#label(known), 1);
      this.#you(pet || this.settings.noName, 2);
    });
    this.#hideReplies();
    this.#miaCharm(2);
  }

  /** A whole new pet: the conversation carries on where it was, the cart keeps what is already in it. */
  #restart() {
    this.#turn += 1;
    this.#species = null;
    this.#breed = null;
    this.#charm = null;
    this.#drawn = false;
    this.#dxf = '';
    this.#ware = null;
    this.#picked = {};
    this.#stones = [];
    this.refs.breedInput.value = '';
    this.refs.nameInput.value = '';
    this.#halo = false;
    if (this.refs.haloBox) this.refs.haloBox.checked = false;
    this.#settle();
    this.#editing = -1;
    this.#goto('pet');
  }

  /** What has been said belongs to the piece just finished: it stays as history, and is edited from the recap. */
  #settle() {
    for (const row of this.refs.thread.children) {
      row.dataset.at = '-1'; // out of reach of the trimming that comes with the next question
      row.dataset.keep = 'true';
      row.querySelector('[data-goto]')?.remove();
    }
  }

  /** The same charm on a second piece: only the piece and its options start over. */
  #againOnAnotherPiece() {
    this.#turn += 1;
    this.#ware = null;
    this.#picked = {};
    this.#stones = [];
    this.#editing = -1; // a second piece, not a change to the first
    this.#goto('product');
  }

  // — the thread ————————————————————————————————————————————————

  /** A line from Mia: she reads, then the dots, then the bubble — the way a person writes. */
  #mia(text, at, keep = false) {
    if (!text) return this.#queue;
    const turn = this.#turn;
    this.#queue = this.#queue.then(async () => {
      if (turn !== this.#turn) return; // the buyer went back: this line is no longer true
      const nothingToRead = this.refs.thread.children.length === 0; // her opening line
      await this.#pause(nothingToRead ? 0 : this.#readingTime());
      if (turn !== this.#turn) return;
      const dots = this.#showTyping(at);
      await this.#pause(this.#writingTime(text));
      dots.remove();
      if (turn !== this.#turn) return;

      const bubble = document.createElement('p');
      bubble.className = 'pawbau__bubble pawbau__bubble--mia';
      bubble.textContent = text;
      this.#fromMia(bubble, at, keep);
    });
    return this.#queue;
  }

  /** Whatever Mia sends — words, dots, a drawing — goes out beside her face. */
  #fromMia(content, at, keep = false) {
    const row = document.createElement('div');
    row.className = 'pawbau__row';
    row.dataset.at = String(at);
    if (keep) row.dataset.keep = 'true';
    const face = this.refs.avatarTemplate?.content.cloneNode(true);
    if (face) row.append(face);
    row.append(content);
    this.refs.thread.append(row);
    this.#scroll();
    return row;
  }

  #showTyping(at) {
    const dots = document.createElement('p');
    dots.className = 'pawbau__bubble pawbau__bubble--mia pawbau__typing';
    dots.setAttribute('aria-hidden', 'true');
    dots.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));
    return this.#fromMia(dots, at);
  }

  /** What the buyer answered, with a way back to that question. */
  #you(text, at, keep = false) {
    this.#pinned = true; // they just answered: they want to see what comes next
    const bubble = document.createElement('p');
    bubble.className = 'pawbau__bubble pawbau__bubble--you';
    bubble.dataset.answer = 'true';
    bubble.textContent = text + ' ';
    const change = document.createElement('button');
    change.type = 'button';
    change.dataset.goto = STEPS[at];
    change.textContent = 'Change';
    bubble.append(change);
    const row = document.createElement('div');
    row.className = 'pawbau__row pawbau__row--you';
    row.dataset.at = String(at);
    if (keep) row.dataset.keep = 'true';
    row.style.alignSelf = 'flex-end';
    row.append(bubble);
    this.refs.thread.append(row);
    this.#scroll();
  }

  #inAHurry() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }

  /** The beat before the dots: she is reading your answer, not typing yet. */
  #readingTime() {
    if (this.#inAHurry()) return 0;
    const [low, high] = READING_MS;
    return low + Math.random() * (high - low);
  }

  /** Long lines take longer to type, with the unevenness of a real hand. */
  #writingTime(text) {
    if (this.#inAHurry()) return 0;
    const paced = TYPING_MIN + text.length * PER_CHAR_MS;
    return Math.min(TYPING_MAX, paced) * (0.85 + Math.random() * 0.3);
  }

  #pause(ms) {
    return ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
  }

  /** Follow the conversation, unless the buyer has scrolled up to read something. */
  #scroll() {
    if (!this.#pinned) return;
    const { thread } = this.refs;
    thread.scrollTo({ top: thread.scrollHeight, behavior: 'smooth' });
  }

  #watchScrolling() {
    const { thread } = this.refs;
    thread.addEventListener(
      'scroll',
      () => {
        this.#pinned = thread.scrollHeight - thread.scrollTop - thread.clientHeight < 90;
      },
      { passive: true }
    );
  }

  /** Once the button has scrolled away, the same button waits at the bottom of the screen. */
  #watchTheButton() {
    const { openButton, dock } = this.refs;
    if (!dock || !openButton || typeof IntersectionObserver !== 'function') return;

    const watch = new IntersectionObserver(
      ([entry]) => {
        dock.hidden = entry.isIntersecting || this.refs.panel?.open === true;
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    watch.observe(openButton);

    // while the conversation is open the page behind it is not the buyer's problem
    this.refs.panel?.addEventListener('close', () => {
      dock.hidden = openButton.getBoundingClientRect().bottom > 0;
    });
  }

  /** Everything said after this step goes away, and this step is asked again from scratch. */
  #trimThread(at) {
    for (const row of [...this.refs.thread.children]) {
      const when = Number(row.dataset.at ?? -1);
      if (when > at || (when === at && !row.dataset.keep)) row.remove();
    }
  }

  #goto(step) {
    window.pbEv?.(`chat_${step}`);
    // the name is settled once the piece is asked: the same milestone the Studio reports as "named"
    if (step === 'product') window.pbEv?.('named');
    const at = STEPS.indexOf(step);
    if (at < 0) return;

    this.#trimThread(at);
    this.#step = step;
    this.#hideReplies();

    const line = {
      pet: this.#made.length ? this.settings.sayPetAgain || this.settings.sayPet : this.settings.sayPet,
      breed: this.settings.sayBreed,
      name: this.settings.sayName,
      product: this.settings.sayProduct,
      options: this.#optionQuestion(),
      stones: this.settings.sayStones,
      gift: this.settings.sayGift,
      done: this.settings.sayDone,
    }[step];
    let said = line ? this.#mia(line, at) : this.#queue;
    // the gift box is easier to say yes to once you have seen it
    if (step === 'gift' && this.settings.giftImage) said = this.#showGiftPhoto(at);
    // the photo is fetched while the buyer is still choosing, so it lands at once
    if (step === 'stones' || step === 'options') this.#preloadGiftPhoto();
    // con un pezzo gia nel carrello il secondo costa meno: si dice prima dei bottoni
    if (step === 'done' && this.#made.length > 0 && this.settings.bundlePrice) said = this.#sayBundle(at);
    // the gift list has its own lines for another piece: the two buttons would say it twice
    if (step === 'done') {
      for (const ref of ['againProduct', 'againDesign']) if (this.refs[ref]) this.refs[ref].hidden = this.#asList();
    }

    this.#say('');

    const turn = this.#turn;
    said.then(() => {
      if (turn !== this.#turn || this.#step !== step) return;
      this.#showReply(step);
      this.#scroll();
    });
  }

  /** The season's tip, right after hello (sections/pawbau-campaign.liquid): a small title and one line. */
  #sayTip(at) {
    const camp = window.PawbauCampaign;
    const c = camp?.active();
    if (!c?.chatTip) return this.#queue;
    const html = camp.text(c.chatTip, c);
    const turn = this.#turn;
    this.#queue = this.#queue.then(async () => {
      if (turn !== this.#turn) return;
      await this.#pause(this.#readingTime());
      if (turn !== this.#turn) return;
      const dots = this.#showTyping(at);
      await this.#pause(this.#writingTime(html.replace(/<[^>]+>/g, '')));
      dots.remove();
      if (turn !== this.#turn) return;
      const bubble = document.createElement('div');
      bubble.className = `pawbau__bubble pawbau__bubble--mia pawbau__tip${c.look === 'deal' ? ' pawbau__tip--deal' : ''}`;
      if (c.kicker) {
        const kicker = document.createElement('span');
        kicker.className = 'pawbau__tip-kicker';
        kicker.textContent = c.kicker;
        bubble.append(kicker);
      }
      const line = document.createElement('span');
      line.innerHTML = html; // built by PawbauCampaign.text from escaped editor text
      bubble.append(line);
      this.#fromMia(bubble, at, true);
    });
    return this.#queue;
  }

  /** The gift box, shown: Mia sends the photo right after she asks. */
  #showGiftPhoto(at) {
    const turn = this.#turn;
    this.#queue = this.#queue.then(async () => {
      if (turn !== this.#turn) return;
      await this.#pause(this.#inAHurry() ? 0 : 450);
      if (turn !== this.#turn) return;
      const figure = document.createElement('figure');
      figure.className = 'pawbau__bubble pawbau__bubble--mia pawbau__photo';
      const img = document.createElement('img');
      img.src = this.settings.giftImage;
      img.alt = this.settings.giftImageAlt || '';
      img.width = 900;
      img.height = 382;
      img.decoding = 'async';
      img.addEventListener('load', () => this.#scroll(), { once: true });
      figure.append(img);
      this.#fromMia(figure, at);
    });
    return this.#queue;
  }

  /** Asked for early, so the photo is already here when Mia sends it. */
  #preloadGiftPhoto() {
    if (!this.settings.giftImage || this.#giftPhoto) return;
    this.#giftPhoto = new Image();
    this.#giftPhoto.src = this.settings.giftImage;
  }

  /** The bundle, once the first piece is theirs: what the next one costs, and where it is taken off. */
  #sayBundle(at) {
    if (this.#asList()) return this.#sayList(at);
    const turn = this.#turn;
    const titleText = this.#bundleHeadline();
    const text = `${titleText} ${this.settings.bundleLine || ''}`;
    this.#queue = this.#queue.then(async () => {
      if (turn !== this.#turn) return;
      await this.#pause(this.#readingTime());
      if (turn !== this.#turn) return;
      const dots = this.#showTyping(at);
      await this.#pause(this.#writingTime(text));
      dots.remove();
      if (turn !== this.#turn) return;

      const bubble = document.createElement('div');
      bubble.className = 'pawbau__bubble pawbau__bubble--mia pawbau__bundle';

      const line = document.createElement('p');
      const title = document.createElement('b');
      title.textContent = titleText;
      line.append(title, ` ${this.settings.bundleLine || ''} `);

      const tag = document.createElement('span');
      tag.className = 'pawbau__bundle-price';
      tag.append(this.settings.bundlePrice || '');
      const was = this.#made.at(-1)?.ware?.price;
      if (was) {
        const before = document.createElement('s');
        before.textContent = this.#money(was);
        tag.append(' ', before);
      }
      line.append(tag);
      bubble.append(line);

      const note = this.#bundleNote();
      if (note) bubble.append(note);
      this.#fromMia(bubble, at);
    });
    return this.#queue;
  }

  /** «[name]'s [piece] is ready»: the bundle's headline, about the piece just made, by name. */
  #bundleHeadline() {
    const last = this.#made.at(-1);
    const title = this.settings.bundleTitle || '';
    // a piece without a name is simply theirs: «Your keychain is ready»
    return (last?.name ? title.replace('[name]', last.name) : title.replace(/\[name\]'s/, 'Your').replace('[name]', ''))
      .replace('[piece]', (last?.ware?.key || 'piece').toLowerCase());
  }

  /** Under the offer: what the season says (a deal's end, the arrival date), otherwise the usual note. */
  #bundleNote() {
    const camp = window.PawbauCampaign;
    const c = camp?.active();
    const note = document.createElement('small');
    if (c?.dealNote) note.innerHTML = camp.text(c.dealNote, c); // escaped editor text with its placeholders
    else if (this.settings.bundleNote) note.textContent = this.settings.bundleNote;
    else return null;
    return note;
  }

  /** The bundle as a gift list ("How Mia offers it" in the editor), once a piece is in the cart. */
  #asList() {
    return this.settings.bundleLook === 'list' && this.#made.length > 0 && Boolean(this.settings.bundlePrice);
  }

  /** Mia says the headline, then sends the list, the way she sends the gift box photo after asking. */
  #sayList(at) {
    this.#mia(this.#bundleHeadline(), at);
    const turn = this.#turn;
    this.#queue = this.#queue.then(async () => {
      if (turn !== this.#turn) return;
      await this.#pause(this.#inAHurry() ? 0 : 450);
      if (turn !== this.#turn) return;
      this.#fromMia(this.#giftList(), at);
    });
    return this.#queue;
  }

  /** One ticked line for each piece made, then the two ways to add one, at the extra-piece price. */
  #giftList() {
    const list = document.createElement('div');
    list.className = 'pawbau__bubble pawbau__bubble--mia pawbau__wish';

    const head = document.createElement('div');
    head.className = 'pawbau__wish-head';
    if (this.settings.listTitle) {
      const title = document.createElement('p');
      title.className = 'pawbau__wish-title';
      title.textContent = this.settings.listTitle;
      head.append(title);
    }
    const stamp = this.#promoStamp();
    if (stamp) {
      const mark = document.createElement('span');
      mark.className = 'pawbau__wish-stamp';
      mark.textContent = stamp;
      head.append(mark);
    }
    if (head.childElementCount) list.append(head);

    const lines = document.createElement('ul');
    lines.className = 'pawbau__wish-lines';
    // the first piece at its own price, every one after it at the extra-piece price
    this.#made.forEach((entry, index) => {
      const price = index === 0 ? (entry.ware?.price ? this.#money(entry.ware.price) : '') : this.settings.bundlePrice;
      lines.append(this.#listLine(this.#pieceTitle(entry), price));
    });

    const last = this.#made.at(-1);
    const ways = [
      [(this.settings.listSame || '').replace('[name]', last?.name || 'design'), () => this.#againOnAnotherPiece()],
      [this.settings.listOther || '', () => this.#restart()],
    ];
    for (const [text, go] of ways) {
      if (!text) continue;
      const line = this.#listLine(text, this.settings.bundlePrice, go);
      line.querySelector('button')?.addEventListener('click', () => {
        // the list has done its job: the line picked is ticked, the other one rests
        for (const other of lines.querySelectorAll('.pawbau__wish-pick')) {
          other.disabled = true;
          if (other.parentElement !== line) other.parentElement.classList.add('pawbau__wish-line--rest');
        }
        line.classList.add('pawbau__wish-line--done');
        go();
      });
      lines.append(line);
    }
    list.append(lines);

    const note = this.#bundleNote();
    if (note) list.append(note);
    return list;
  }

  /** A line of the list: ticked when it is already made, a button when `go` adds it. */
  #listLine(text, price, go = null) {
    const line = document.createElement('li');
    line.className = go ? 'pawbau__wish-line' : 'pawbau__wish-line pawbau__wish-line--done';
    let body = line;
    if (go) {
      body = document.createElement('button');
      body.type = 'button';
      body.className = 'pawbau__wish-pick';
      line.append(body);
    }
    const box = document.createElement('span');
    box.className = 'pawbau__wish-box';
    box.setAttribute('aria-hidden', 'true');
    const words = document.createElement('span');
    words.className = 'pawbau__wish-text';
    words.textContent = text;
    body.append(box, words);
    if (price) {
      const tag = document.createElement('span');
      tag.className = go ? 'pawbau__bundle-price' : 'pawbau__wish-price';
      tag.textContent = price;
      body.append(tag);
    }
    return line;
  }

  /** «Cooper's necklace», or «Your necklace» for a piece without a name. */
  #pieceTitle(entry) {
    const piece = (entry.ware?.key || 'piece').toLowerCase();
    return entry.name ? `${entry.name}'s ${piece}` : `Your ${piece}`;
  }

  /** While the promo bar runs (sections/pawbau-promo-countdown.liquid), its stamp goes on the gift list. */
  #promoStamp() {
    if (!document.documentElement.classList.contains('pcd-on')) return '';
    return document.querySelector('[data-pcd]')?.dataset.stamp || '';
  }

  #hideReplies() {
    for (const name of ['replyPet', 'replyBreed', 'replyName', 'replyProduct', 'replyOptions', 'replyStones', 'replyGift', 'replyDone']) {
      if (this.refs[name]) this.refs[name].hidden = true;
    }
  }

  /** The name people give that breed most often: a better hint than a generic one. */
  #hintName(breed) {
    const { nameInput } = this.refs;
    if (!nameInput) return;
    const righe = (this.settings.nameHints || '').split('\n');
    for (const riga of righe) {
      const [razza, nome] = riga.split(':').map((v) => v.trim());
      if (razza && nome && razza === breed) {
        nameInput.placeholder = nome;
        return;
      }
    }
    nameInput.placeholder = this.settings.namePlaceholder || '';
  }

  #showReply(step) {
    const which = {
      pet: 'replyPet',
      breed: 'replyBreed',
      name: 'replyName',
      product: 'replyProduct',
      options: 'replyOptions',
      stones: 'replyStones',
      gift: 'replyGift',
      done: 'replyDone',
    }[step];
    if (which && this.refs[which]) this.refs[which].hidden = false;
    if (step === 'breed') this.refs.breedInput.focus();
    if (step === 'name') {
      this.#hintName(this.#breed);
      this.#drawHaloCard(this.#breed);
      this.refs.nameInput.focus();
    }
    if (step === 'product') this.#fillCarousel();
    if (step === 'options') { if (this.#pairs()) this.#fillPair(); else this.#fillChoices(); }
    if (step === 'stones') this.#fillMonths();
    if (step === 'gift') this.#priceTheGift();
    if (step === 'stones' || step === 'gift') this.#prepareEarly();
  }

  #onChangeClick = (event) => {
    const change = /** @type {HTMLElement} */ (event.target).closest('[data-goto]');
    if (change) this.#rewind(change.dataset.goto);
  };

  /** Going back to a question drops what depended on it. */
  #rewind(step) {
    this.#turn += 1;
    const at = STEPS.indexOf(step);
    if (at <= STEPS.indexOf('pet')) {
      this.#species = null;
      this.refs.nameInput.value = '';
      this.#halo = false;
      if (this.refs.haloBox) this.refs.haloBox.checked = false;
    }
    if (at <= STEPS.indexOf('breed')) {
      this.#breed = null;
      this.refs.breedInput.value = '';
    }
    if (at <= STEPS.indexOf('name')) {
      this.#drawn = false;
      this.#charm = null;
      this.#dxf = '';
    }
    if (at <= STEPS.indexOf('product')) {
      this.#ware = null;
      this.#picked = {};
    }
    const opzioni = STEPS.indexOf('options');
    if (at <= opzioni) {
      this.#picked = {};
      // le risposte tenute da parte tornano cancellabili: le rifara da capo
      for (const row of this.refs.thread.children) {
        if (Number(row.dataset.at ?? -1) === opzioni) delete row.dataset.keep;
      }
    }
    if (at <= STEPS.indexOf('stones')) this.#stones = [];
    this.#goto(step);
  }

  // — step 1: dog or cat ————————————————————————————————————————

  #onSpeciesClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-species]');
    if (!button) return;
    this.#species = button.dataset.species;
    this.#breed = null;
    this.refs.breedInput.value = '';
    this.#you(this.#speciesLabel(this.#species), 0);
    this.#fillPopular();
    this.#goto('breed');
  };

  #fillPopular() {
    const { popular, popularHint } = this.refs;
    if (!popular) return;
    const raw = { cat: this.settings.popularCats, horse: this.settings.popularHorses }[this.#species] || this.settings.popularDogs;
    const wanted = (raw || '')
      .split(',')
      .map((name) => name.trim().replace(/\s+/g, '-'))
      .filter((name) => name && this.#breeds.includes(name));

    popular.replaceChildren();
    for (const breed of wanted) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pawbau__pick';
      button.dataset.breed = breed;

      const art = document.createElement('span');
      art.className = 'pawbau__pick-art';
      const name = document.createElement('span');
      name.className = 'pawbau__pick-name';
      name.textContent = this.#label(breed);

      button.append(art, name);
      button.addEventListener('click', () => this.#pick(breed));
      popular.append(button);
    }
    popular.hidden = wanted.length === 0;
    if (popularHint) popularHint.hidden = wanted.length === 0;
    popular.scrollLeft = 0;
    this.#decoratePicks(wanted);
  }

  /** Every card shows the shape it stands for, same as the search results. */
  async #decoratePicks(breeds) {
    const shapes = await this.#shapes(breeds);
    for (const shape of shapes) {
      const art = this.refs.popular?.querySelector(`[data-breed="${CSS.escape(shape.breed)}"] .pawbau__pick-art`);
      if (art && !art.querySelector('svg')) art.append(this.#svg(shape.viewBox, shape.path, shape.breed));
    }
  }

  // — step 2: the breed —————————————————————————————————————————

  #onBreedInput = () => {
    const typed = this.refs.breedInput.value.trim().toLowerCase();
    if (!typed) return this.#closeList();

    const starts = [];
    const contains = [];
    for (const breed of this.#ofSpecies()) {
      const label = this.#label(breed).toLowerCase();
      if (label.startsWith(typed)) starts.push(breed);
      else if (label.includes(typed)) contains.push(breed);
      if (starts.length >= MAX_SUGGESTIONS) break;
    }
    const found = [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
    this.#showList(found);
    if (found.length) this.#say('');
    else this.#warn(this.settings.noMatch);
  };

  #showList(breeds) {
    const { breedList, breedInput } = this.refs;
    breedList.replaceChildren();
    this.#highlighted = -1;

    if (!breeds.length) return this.#closeList();

    const visti = new Map();
    for (const breed of breeds) {
      const item = document.createElement('li');
      const nome = this.#label(breed);
      const quante = (visti.get(nome) || 0) + 1;
      visti.set(nome, quante);
      item.textContent = quante > 1 ? `${nome} ${quante}` : nome;
      item.setAttribute('role', 'option');
      item.dataset.breed = breed;
      const choose = (event) => {
        event.preventDefault(); // mousedown would otherwise blur the field
        this.#pick(breed);
      };
      item.addEventListener('mousedown', choose);
      item.addEventListener('click', choose);
      breedList.append(item);
    }
    breedList.hidden = false;
    breedInput.setAttribute('aria-expanded', 'true');
    this.#decorate(breeds);
  }

  /** The silhouette arrives beside each suggestion as soon as it is drawn. */
  async #decorate(breeds) {
    const shapes = await this.#shapes(breeds);
    for (const shape of shapes) {
      const item = this.refs.breedList.querySelector(`[data-breed="${CSS.escape(shape.breed)}"]`);
      if (item && !item.querySelector('svg')) item.prepend(this.#svg(shape.viewBox, shape.path, shape.breed));
    }
  }

  #closeList() {
    const { breedList, breedInput } = this.refs;
    breedList.hidden = true;
    breedList.replaceChildren();
    breedInput.setAttribute('aria-expanded', 'false');
    this.#highlighted = -1;
  }

  #onBreedKeys = (event) => {
    const items = [...this.refs.breedList.children];
    if (!items.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#highlighted = (this.#highlighted + step + items.length) % items.length;
      items.forEach((item, i) => item.setAttribute('aria-selected', String(i === this.#highlighted)));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.#pick(items[Math.max(this.#highlighted, 0)].dataset.breed);
    } else if (event.key === 'Escape') {
      event.stopPropagation(); // the list closes, the panel stays open
      this.#closeList();
    }
  };

  #pick(breed) {
    this.#breed = breed;
    this.#species ??= this.#speciesOf(breed);
    this.refs.breedInput.value = this.#label(breed);
    this.#closeList();
    this.#you(this.#label(breed), 1);
    this.#goto('name');
  }

  // — step 3: the name, and the charm ———————————————————————————

  /**
   * Il nome finisce inciso come lo scrivono: "mia" uscirebbe minuscolo e "COOPER" urlato.
   * Ogni parola prende l'iniziale grande e il resto piccolo, e il cursore resta dov'era.
   */
  #capitalise() {
    const field = this.refs.nameInput;
    if (!field) return;
    const value = field.value;
    // lo spazio e il trattino aprono una parola nuova: «ray ray» e «jean-luc»
    const fixed = value.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, apre, lettera) => apre + lettera.toUpperCase());
    if (fixed === value) return;
    const start = field.selectionStart;
    const end = field.selectionEnd;
    field.value = fixed;
    // il maiuscolo puo cambiare la lunghezza in qualche alfabeto: in quel caso si va in fondo
    if (start !== null) {
      if (fixed.length === value.length) field.setSelectionRange(start, end);
      else field.setSelectionRange(fixed.length, fixed.length);
    }
  }

  #onNameInput = () => {
    this.#capitalise();
    // A different name means a different charm: whatever was drawn no longer counts.
    this.#drawn = false;
    this.#dxf = '';
    this.#picture = '';
    this.#painted = '';
    // a lone space counts as an answer too: it is the charm without a name
    if (this.refs.nameGo) this.refs.nameGo.disabled = !this.refs.nameInput.value;
  };

  /**
   * The name that is engraved. A space, "empty" or "blank" leave it out and the charm is the silhouette
   * alone: the chat never offers it, it is there for whoever asks.
   */
  #petName() {
    const name = this.refs.nameInput.value.trim();
    return /^(empty|blank)$/i.test(name) ? '' : name;
  }

  /** The halo belongs to the charm, so asking for it throws away whatever was drawn. */
  #onHaloChange = () => {
    this.#halo = Boolean(this.refs.haloBox?.checked);
    this.#drawn = false;
    this.#charm = null;
    this.#dxf = '';
    this.#picture = '';
    this.#painted = '';
  };

  #onNameKeys = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#confirmName();
    }
  };

  /** The name is said, then Mia draws it and the charm arrives as her next message. */
  #confirmName() {
    if (!this.refs.nameInput.value || !this.#breed) return;
    this.#you(this.#petName() || this.settings.noName, 2);
    this.#hideReplies();
    this.#miaCharm(2);
  }

  /** Mia draws the charm and sends it like any other message: the dots cover the work. */
  #miaCharm(at) {
    const turn = this.#turn;
    const breed = this.#breed;
    const name = this.#petName();

    this.#mia(this.settings.drawingLine || '', at);

    this.#queue = this.#queue.then(async () => {
      if (turn !== this.#turn) return;
      await this.#pause(this.#readingTime());
      if (turn !== this.#turn) return;
      const waiting = await this.#showDrawing(at, breed);
      // long enough to watch the outline appear, even when the engine answers at once
      const [charm] = await Promise.all([this.#fetchCharm(breed, name, this.#halo), this.#pause(1500)]);
      waiting.remove();
      if (turn !== this.#turn) return;

      if (!charm) {
        this.#mia(this.settings.drawingFailed || '', at);
        this.#goto('name'); // otherwise there is nothing left to answer with
        return;
      }

      this.#charm = charm;
      this.#showCharm(at, breed);

      if (charm.fits === false) {
        this.#mia(this.settings.tooLong || '', at);
        this.#goto('name');
        return;
      }

      this.#drawn = true;
      this.#trackDrawing(name, breed);
      this.#goto('product');
    });
    return this.#queue;
  }

  /** The wait itself: the breed's outline draws and redraws until the charm is ready. */
  async #showDrawing(at, breed) {
    const holder = document.createElement('div');
    holder.className = 'pawbau__bubble pawbau__bubble--mia pawbau__drawing';
    holder.setAttribute('aria-label', 'Drawing');
    const row = this.#fromMia(holder, at);

    const [shape] = await this.#shapes([breed]);
    if (!shape || !row.isConnected) return row;

    const svg = this.#svg(shape.viewBox, shape.path, breed);
    holder.append(svg);
    for (const path of svg.querySelectorAll('path')) {
      const length = path.getTotalLength();
      path.style.strokeDasharray = String(length);
      path.style.setProperty('--len', String(length));
    }
    this.#scroll();
    return row;
  }

  /** The finished charm, with the stones beside it once there are any. */
  #showCharm(at, breed) {
    const bubble = document.createElement('div');
    bubble.className = 'pawbau__bubble pawbau__bubble--mia pawbau__charm';
    bubble.append(this.#svg(this.#charm.viewBox, this.#charm.path, breed));
    this.#charmBubble = bubble;
    this.#fromMia(bubble, at);
  }

  /** Breed silhouettes, asked once and kept: thumbnails, shortcuts, and the drawing animation. */
  async #shapes(breeds, rough = true) {
    const missing = breeds.filter((breed) => !this.#shapeCache.has(breed));
    if (missing.length) {
      try {
        const url = `${this.engine}/shapes?breeds=${encodeURIComponent(missing.join(','))}&rough=${rough ? 1 : 0}`;
        const res = await fetch(url);
        const data = await res.json();
        for (const shape of data.shapes || []) this.#shapeCache.set(shape.breed, shape);
      } catch {
        // thumbnails are a nicety: the list still works without them
      }
    }
    return breeds.map((breed) => this.#shapeCache.get(breed)).filter(Boolean);
  }

  /** Their breed with the halo on, drawn inside the card: nobody has to imagine it. */
  async #drawHaloCard(breed) {
    const { haloArt } = this.refs;
    if (!haloArt || !breed) return;
    let shape = this.#haloCache.get(breed);
    if (!shape) {
      try {
        const url = `${this.engine}/shape?breed=${encodeURIComponent(breed)}&rough=1&halo=1`;
        const res = await fetch(url);
        if (!res.ok) return;
        shape = await res.json();
        this.#haloCache.set(breed, shape);
      } catch {
        return; // senza disegno la card resta, con le sue parole
      }
    }
    if (this.#breed !== breed) return; // hanno gia cambiato razza
    haloArt.replaceChildren(this.#svg(shape.viewBox, shape.path, breed));
  }

  async #fetchCharm(breed, name, halo = false) {
    try {
      const url = `${this.engine}/preview?breed=${encodeURIComponent(breed)}&name=${encodeURIComponent(name)}${halo ? '&halo=1' : ''}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      return await res.json();
    } catch {
      return null;
    }
  }

  #svg(viewBox, pathData, breed) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `${this.#label(breed)} charm`);
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('fill-rule', 'evenodd');
    svg.append(path);
    return svg;
  }

  // — step 4: the piece —————————————————————————————————————————

  #fillCarousel() {
    const { carousel } = this.refs;
    if (!carousel) return;
    carousel.replaceChildren();

    for (const ware of this.#catalogue.products || []) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'pawbau__ware';
      card.dataset.key = ware.key;

      if (ware.image) {
        const img = document.createElement('img');
        img.src = ware.image;
        img.alt = '';
        img.loading = 'lazy';
        card.append(img);
      } else {
        // no photograph yet: a drawn stand-in, so the choice still reads at a glance
        const icon = this.refs[`icon${ware.key[0].toUpperCase()}${ware.key.slice(1)}`];
        if (icon) card.append(icon.content.cloneNode(true));
      }
      const title = document.createElement('b');
      // a narrow card says only which piece it is ("Necklace"), so the name does not run to three lines
      const long = document.createElement('span');
      long.className = 'pawbau__ware-long';
      long.textContent = wareName(ware);
      const short = document.createElement('span');
      short.className = 'pawbau__ware-short';
      short.textContent = wareName(ware, true);
      title.append(long, short);
      const price = document.createElement('span');
      const deal = this.#made.length > 0 || this.#adding ? this.settings.bundlePrice : '';
      if (deal) {
        price.append(deal, ' ');
        const was = document.createElement('s');
        was.textContent = this.#money(ware.price);
        price.append(was);
      } else {
        price.textContent = this.#money(ware.price);
      }
      card.append(title, price);
      card.addEventListener('click', () => this.#chooseWare(ware));
      carousel.append(card);
    }
  }

  #chooseWare(ware) {
    if (this.#ware && this.#ware.key !== ware.key) this.#dxf = '';    // gli anelli cambiano posto: il file va rifatto
    this.#ware = ware;
    this.#picked = {};
    this.#you(wareName(ware), 3);
    this.#goto('options');
  }

  // — step 5: colour, and cord when the piece has one ———————————

  /** The next option this piece still needs, if any. */
  #nextOption() {
    return (this.#ware?.options || []).find((name) => !this.#picked[name]) || null;
  }

  #optionQuestion() {
    // due scelte si chiedono in una volta sola, perche vanno viste insieme
    if (this.#pairs()) return this.settings.askPair || this.settings.askColor;
    const option = this.#nextOption();
    if (!option) return null;
    return option.toLowerCase() === 'cord' ? this.settings.askCord : this.settings.askColor;
  }

  /** Se il pezzo chiede piu di una cosa, si mostra il modulo doppio invece di due domande. */
  #pairs() {
    return (this.#ware?.options || []).length > 1;
  }

  #fillChoices() {
    const { choices, pair } = this.refs;
    const option = this.#nextOption();
    if (pair) pair.hidden = true;
    if (!choices || !option) return;
    choices.hidden = false;
    choices.replaceChildren();

    const index = this.#ware.options.indexOf(option);
    const seen = new Set();
    for (const variant of this.#ware.variants) {
      const value = variant.options[index];
      if (!value || seen.has(value)) continue;
      // only values that can still be reached with what is already chosen
      const reachable = this.#ware.options.every((name, i) => {
        const chosen = this.#picked[name];
        return !chosen || variant.options[i] === chosen;
      });
      if (!reachable) continue;
      seen.add(value);

      const button = this.#swatch(option, value, seen.size - 1);
      button.addEventListener('click', () => this.#chooseOption(option, value));
      choices.append(button);
    }
    // quattro su tre colonne lascerebbero l'ultima sola in fondo: due e due riempie le righe
    choices.style.setProperty('--colonne', seen.size === 4 ? 2 : 3);
    this.#sortChoices();
  }

  /** The order they are shown in: the one most people pick sits where the thumb lands. */
  #sortChoices() {
    const { choices } = this.refs;
    const wanted = (this.settings.choiceOrder || '').split(',').map((v) => v.trim()).filter(Boolean);
    if (!wanted.length || !choices) return;
    const cards = [...choices.children];
    cards.sort((a, b) => {
      const ia = wanted.indexOf(a.dataset.value);
      const ib = wanted.indexOf(b.dataset.value);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
    for (const card of cards) choices.append(card);
  }

  /** Their charm cast in one of the metals, with a shine that crosses it every few seconds. */
  #inThatMetal(colour, order) {
    const { viewBox, path } = this.#charm;
    const [, , width, height] = viewBox.split(/\s+/).map(Number);
    const w = width || 32;
    const h = height || 20;
    const id = `pawbau-shine-${order}-${Math.round(Math.random() * 1e6)}`;

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('pawbau__swatch-art');

    const defs = document.createElementNS(SVG_NS, 'defs');

    const clip = document.createElementNS(SVG_NS, 'clipPath');
    clip.id = id;
    const cut = document.createElementNS(SVG_NS, 'path');
    cut.setAttribute('d', path);
    cut.setAttribute('clip-rule', 'evenodd');
    clip.append(cut);

    const gradient = document.createElementNS(SVG_NS, 'linearGradient');
    gradient.id = `${id}-sheen`;
    for (const [offset, opacity] of [[0, 0], [0.5, 0.9], [1, 0]]) {
      const stop = document.createElementNS(SVG_NS, 'stop');
      stop.setAttribute('offset', String(offset));
      stop.setAttribute('stop-color', '#FFFFFF');
      stop.setAttribute('stop-opacity', String(opacity));
      gradient.append(stop);
    }
    defs.append(clip, gradient);

    const body = document.createElementNS(SVG_NS, 'path');
    body.setAttribute('d', path);
    body.setAttribute('fill', colour);
    body.setAttribute('fill-rule', 'evenodd');

    const band = document.createElementNS(SVG_NS, 'rect');
    band.setAttribute('x', String(-w * 0.45));
    band.setAttribute('y', String(-h * 0.2));
    band.setAttribute('width', String(w * 0.34));
    band.setAttribute('height', String(h * 1.6));
    band.setAttribute('fill', `url(#${id}-sheen)`);
    if (!this.#inAHurry()) {
      const move = document.createElementNS(SVG_NS, 'animateTransform');
      move.setAttribute('attributeName', 'transform');
      move.setAttribute('type', 'translate');
      move.setAttribute('from', '0 0');
      move.setAttribute('to', `${w * 1.7} 0`);
      move.setAttribute('dur', '3s');
      move.setAttribute('begin', `${order * 0.4}s`);
      move.setAttribute('repeatCount', 'indefinite');
      band.append(move);
    }

    const tilt = document.createElementNS(SVG_NS, 'g');
    tilt.setAttribute('transform', 'skewX(-16)');
    tilt.append(band);

    const window_ = document.createElementNS(SVG_NS, 'g');
    window_.setAttribute('clip-path', `url(#${id})`);
    window_.append(tilt);

    svg.append(defs, body, window_);
    return svg;
  }

  /** Una scelta da toccare: il loro ciondolo in quel metallo, o il pallino di quel colore. */
  #swatch(option, value, at, stella = true) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pawbau__swatch';
    button.dataset.value = value;
    // «Black» e sia una finitura sia una corda: a dire quale e' la domanda, non il nome
    const corda = option.toLowerCase() === 'cord';
    const metal = corda ? null : DRAWN[value];
    const dot = corda ? CORDS[value] : FINISHES[value];
    if (metal && this.#charm) {
      // a metal is better shown on their own charm than on a dot
      button.append(this.#inThatMetal(metal, at));
    } else if (corda && dot) {
      // la corda si riconosce dall'intreccio, non da un pallino
      const chip = draw('svg', { viewBox: '0 0 52 52', class: 'pawbau__cord', 'aria-hidden': 'true' });
      braid(chip, -4, 56, 26, dot, 22);
      button.append(chip);
    } else if (dot) {
      const chip = document.createElement('i');
      chip.style.background = dot;
      button.append(chip);
    }
    button.append(document.createTextNode(value));
    if (stella && value === this.settings.choiceStar && this.settings.starLabel) {
      const star = document.createElement('small');
      star.className = 'pawbau__star';
      star.textContent = this.settings.starLabel;
      button.append(star);
    }
    return button;
  }

  /**
   * Il bracciale chiede due cose, la finitura e la corda, e sceglierle una per volta vuol dire
   * scoprire solo dopo se stanno bene insieme. Qui si scelgono guardandole: sopra il bracciale
   * come verra, sotto le due file.
   */
  #fillPair() {
    const { pair, pairRows, choices } = this.refs;
    if (!pair || !pairRows || !this.#ware) return this.#fillChoices();
    if (choices) choices.hidden = true;
    pair.hidden = false;
    pairRows.replaceChildren();
    /** @type {Record<string, HTMLElement>} each option's row of swatches, to mark the picked one */
    const rows = {};

    this.#ware.options.forEach((option, index) => {
      const valori = [...new Set(this.#ware.variants.map((v) => v.options[index]).filter(Boolean))];
      // si apre gia su un bracciale vero: senza una scelta di partenza il riquadro sarebbe vuoto
      if (!this.#picked[option]) {
        this.#picked[option] = valori.includes(this.settings.choiceStar) ? this.settings.choiceStar : valori[0];
      }

      const blocco = document.createElement('div');
      const nome = document.createElement('span');
      nome.className = 'pawbau__pair-label';
      nome.textContent = option;
      const riga = document.createElement('div');
      // qui lo spazio in altezza e' prezioso: si scorre di lato, e la stellina si toglie
      riga.className = 'pawbau__swatches pawbau__swatches--roll';

      valori.forEach((value, at) => {
        const button = this.#swatch(option, value, at, false);
        button.setAttribute('aria-pressed', String(this.#picked[option] === value));
        button.addEventListener('click', () => {
          this.#picked[option] = value;
          for (const altro of riga.children) {
            altro.setAttribute('aria-pressed', String(altro.dataset.value === value));
          }
          markPicks();
          this.#paintBracelet(this.refs.pairLive);
        });
        riga.append(button);
      });

      rows[option] = riga;
      blocco.append(nome, riga);
      pairRows.append(blocco);
    });

    // the pairs most people choose, for him and for her: one tap sets both rows
    const picks = this.#pairPicks();
    const pickButtons = [];
    const markPicks = () => {
      for (const { button, values } of pickButtons) {
        const on = this.#ware.options.every((option, i) => this.#picked[option] === values[i]);
        button.setAttribute('aria-pressed', String(on));
      }
    };
    if (picks.length) {
      const holder = document.createElement('div');
      holder.className = 'pawbau__pair-picks';
      for (const { label, values } of picks) {
        const fits = this.#ware.options.every((option, i) =>
          this.#ware.variants.some((v) => v.options[i] === values[i])
        );
        if (!fits) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pawbau__pair-pick';
        const title = document.createElement('b');
        title.textContent = label;
        const what = document.createElement('small');
        what.textContent = this.#ware.options.map((option, i) => `${values[i]} ${option.toLowerCase() === 'finish' ? 'charm' : option.toLowerCase()}`).join(' · ');
        button.append(title, what);
        button.addEventListener('click', () => {
          this.#ware.options.forEach((option, i) => {
            this.#picked[option] = values[i];
            for (const altro of rows[option]?.children || []) {
              altro.setAttribute('aria-pressed', String(altro.dataset.value === values[i]));
            }
          });
          markPicks();
          this.#paintBracelet(this.refs.pairLive);
        });
        pickButtons.push({ button, values });
        holder.append(button);
      }
      if (pickButtons.length) pairRows.prepend(holder);
    }
    markPicks();

    this.#paintBracelet(this.refs.pairLive);
  }

  /** «Most chosen for him: Black, Black», one per line: a label, then one value per option, in order. */
  #pairPicks() {
    return (this.settings.pairPicks || '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.includes(':'))
      .map((line) => {
        const at = line.lastIndexOf(':');
        return {
          label: line.slice(0, at).trim(),
          values: line
            .slice(at + 1)
            .split(',')
            .map((value) => value.trim()),
        };
      })
      .filter(({ label, values }) => label && values.length);
  }

  /**
   * Il bracciale disegnato nella corda scelta: l'intreccio ai due lati, il loro ciondolo in mezzo,
   * attaccato alla corda sui due fianchi, e i due capi con le perline nel metallo scelto.
   * Nessuna foto: il disegno vale per ogni colore e per ogni razza.
   */
  #paintBracelet(into) {
    if (!into) return;
    if (!this.#charm) return this.#paintLive(into, null);

    into.replaceChildren();
    const cord = CORDS[this.#picked.Cord] || CORDS.Black;
    const metal = DRAWN[this.#picked.Finish] || '#C39A52';
    const svg = draw('svg', {
      viewBox: '0 0 300 120',
      role: 'img',
      'aria-label': `${this.#picked.Finish} charm on the ${this.#picked.Cord} braided cord`,
    });
    const cy = 60;

    const [vx, vy, w, h] = this.#charm.viewBox.split(/\s+/).map(Number);
    const scale = Math.min(96 / (w || 32), 90 / (h || 24));
    const charmW = (w || 32) * scale;
    const charmH = (h || 24) * scale;
    const charmX = 150 - charmW / 2;
    const charmY = cy - charmH / 2;

    // the charm first, so its outline can be measured; the cord goes in underneath it
    const charm = draw('svg', { x: charmX, y: charmY, width: charmW, height: charmH, viewBox: this.#charm.viewBox }, svg);
    const outline = draw('path', { d: this.#charm.path, fill: metal, 'fill-rule': 'evenodd' }, charm);
    into.append(svg);

    // the cord is tied to the rings welded on the charm's sides, at the height they are welded
    // (HOLDERS.bracelet.seat), so it has to reach the outline there, not the edge of the box
    const seat = HOLDERS.bracelet.seat ?? 0.56;
    const seatY = (vy || 0) + (h || 24) * seat;
    let [minX, maxX] = [Infinity, -Infinity];
    try {
      const total = outline.getTotalLength();
      const band = (h || 24) * 0.07;
      for (let i = 0; i <= 600; i++) {
        const point = outline.getPointAtLength((total * i) / 600);
        if (Math.abs(point.y - seatY) > band) continue;
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
      }
    } catch {
      // a charm that cannot be measured keeps the edges of its box
    }
    const toX = (x) => charmX + (x - (vx || 0)) * scale;
    const left = Number.isFinite(minX) ? toX(minX) + 2 : charmX + 3;
    const right = Number.isFinite(maxX) ? toX(maxX) - 2 : charmX + charmW - 3;
    const y = Number.isFinite(minX) ? charmY + (seatY - (vy || 0)) * scale : cy;

    const cordLayer = draw('g', {}, null);
    svg.insertBefore(cordLayer, charm);
    for (const [end, start] of [[12, 44], [288, 256]]) {
      for (const dy of [-2.2, 2.2]) {
        draw('line', { x1: end, y1: y + dy, x2: start, y2: y + dy, stroke: mix(cord, '#000000', 0.25), 'stroke-width': 1.6, 'stroke-linecap': 'round' }, cordLayer);
        draw('circle', { cx: end, cy: y + dy, r: 2.4, fill: metal }, cordLayer);
      }
    }
    braid(cordLayer, 44, left, y, cord);
    braid(cordLayer, right, 256, y, cord);
  }

  /** Le due scelte si dicono insieme, come sono state fatte: «Gold · Burgundy». */
  #confirmPair = () => {
    if (!this.#ware) return;
    const detto = this.#ware.options.map((o) => this.#picked[o]).filter(Boolean).join(' · ');
    this.#you(detto, 4);
    this.#goto(this.#ware.stones && !this.#adding ? 'stones' : 'gift');
  };

  #chooseOption(option, value) {
    this.#picked[option] = value;
    const ancora = Boolean(this.#nextOption());
    // il bracciale chiede due cose di fila: senza il segno, la domanda sulla corda
    // si porterebbe via la finitura appena scelta, e nel filo non resterebbe traccia
    this.#you(value, 4, ancora);
    if (ancora) {
      this.#goto('options'); // same step, next question
      return;
    }
    this.#goto(this.#ware?.stones && !this.#adding ? 'stones' : 'gift');
  }

  // — step 6: the stones ————————————————————————————————————————

  #fillMonths() {
    const { months, sample, live } = this.refs;
    const stones = this.#ware?.stones;
    if (!months || !stones) return;

    // the shop's own photograph of the piece, once there is one
    if (sample) {
      sample.replaceChildren();
      if (stones.image) {
        const img = document.createElement('img');
        img.src = stones.image;
        img.alt = '';
        img.loading = 'lazy';
        sample.append(img);
        // a photo of someone else's piece: it shows where the stones go, not what they will receive
        if (this.settings.stonesSampleNote) {
          const note = document.createElement('figcaption');
          note.textContent = this.settings.stonesSampleNote;
          sample.append(note);
        }
        sample.hidden = false;
      } else {
        sample.hidden = true;
      }
    }

    months.replaceChildren();
    for (const variant of stones.variants) {
      const { month, stone, meaning, colour } = readStone(variant.name);
      const card = document.createElement('div');
      card.className = 'pawbau__month';
      card.dataset.stone = variant.name;
      card.setAttribute('aria-pressed', String(this.#stones.includes(variant.name)));

      if (variant.image) {
        const img = document.createElement('img');
        img.src = variant.image;
        img.alt = '';
        img.loading = 'lazy';
        card.append(img);
      } else {
        const dot = document.createElement('i');
        dot.style.background = colour;
        card.append(dot);
      }

      const name = document.createElement('b');
      name.textContent = month;
      const kind = document.createElement('em');
      kind.textContent = stone;
      const says = document.createElement('small');
      says.textContent = meaning;

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'pawbau__add';
      add.textContent = this.#addLabel(variant);
      add.addEventListener('click', () => this.#toggleStone(variant.name));

      card.append(name, kind, says, add);
      months.append(card);
    }
    this.#countStones();
    this.#paintLive();
  }

  #addLabel(variant) {
    const chosen = this.#stones.includes(variant.name);
    if (chosen) return this.settings.stonesAdded || 'Added';
    return `${this.settings.stonesAdd || 'Add'} · ${this.#money(variant.price)}`;
  }

  #toggleStone(value) {
    const max = Number(this.settings.stonesMax || 4);
    const at = this.#stones.indexOf(value);
    if (at >= 0) this.#stones.splice(at, 1);
    else if (this.#stones.length < max) this.#stones.push(value);
    else return this.#warn(this.#counter());

    const card = this.refs.months?.querySelector(`[data-stone="${CSS.escape(value)}"]`);
    if (card) {
      card.setAttribute('aria-pressed', String(this.#stones.includes(value)));
      const variant = this.#ware.stones.variants.find((v) => v.name === value);
      const button = card.querySelector('.pawbau__add');
      if (button && variant) button.textContent = this.#addLabel(variant);
    }
    this.#countStones();
    this.#paintLive();
  }

  #counter() {
    const max = Number(this.settings.stonesMax || 4);
    return (this.settings.stonesCount || '[n] of [max] added')
      .replace('[n]', String(this.#stones.length))
      .replace('[max]', String(max));
  }

  #countStones() {
    if (this.refs.stonesCount) this.refs.stonesCount.textContent = this.#counter();
    this.#say('');
    if (this.refs.stonesGo) this.refs.stonesGo.disabled = this.#stones.length === 0;
  }

  /** The buyer's own piece, drawn: the chain, their charm hanging from it, the stones threaded beside it. */
  #paintLive(into = this.refs.live, hint = this.refs.liveHint) {
    const live = into;
    const liveHint = hint;
    if (!live) return;
    live.querySelector('svg')?.remove();

    const kind = this.#ware?.key || 'necklace';
    const holder = HOLDERS[kind] || HOLDERS.necklace;
    const metal = FINISHES[this.#picked.Finish] || '#CDA35D';
    const cord = CORDS[this.#picked.Cord] || null;

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 200 120');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', 'Your piece with the stones you picked');

    const line = document.createElementNS(SVG_NS, 'path');
    line.setAttribute('d', holder.line);
    line.setAttribute('fill', 'none');
    line.setAttribute('stroke', cord || metal);
    line.setAttribute('stroke-width', String(holder.thickness));
    line.setAttribute('stroke-linecap', 'round');
    svg.append(line);
    live.prepend(svg); // into the page before measuring, or there is nothing to measure

    const along = this.#along(line, holder);
    let hangs = along(0);

    // the keychain: its chain, link after link, from the bottom of the ring to the charm
    if (holder.chain) {
      const { x, top, bottom } = holder.chain;
      for (let y = top, i = 0; y < bottom; y += 3.6, i++) {
        const link = document.createElementNS(SVG_NS, 'ellipse');
        link.setAttribute('cx', String(x));
        link.setAttribute('cy', String(y + 1.8));
        link.setAttribute('rx', i % 2 ? '0.9' : '1.9');
        link.setAttribute('ry', '2.3');
        link.setAttribute('fill', 'none');
        link.setAttribute('stroke', metal);
        link.setAttribute('stroke-width', '1');
        svg.append(link);
      }
      hangs = { x, y: bottom };
    }

    let charmW = 0;
    if (this.#charm) {
      const [, , w, h] = this.#charm.viewBox.split(/\s+/).map(Number);
      const scale = holder.charm / Math.max(w || 32, h || 24);
      charmW = (w || 32) * scale;
      const charmH = (h || 24) * scale;
      // the bracelet wears its charm inside the strap, held by a ring on each side; everything else hangs it below
      const inside = Boolean(holder.sides);

      if (inside) {
        // the strap stops at the charm and starts again after it, or it would run straight through the dog
        const total = line.getTotalLength?.() || 0;
        if (total) line.setAttribute('stroke-dasharray', `${(total - (charmW - 4)) / 2} ${charmW - 4}`);
      }

      const jumps = inside ? [-1, 1] : [0];
      for (const side of jumps) {
        const seat = side ? along(side * (charmW / 2 - 1)) : { x: hangs.x, y: hangs.y + 2 };
        const ring = document.createElementNS(SVG_NS, 'circle');
        ring.setAttribute('cx', String(seat.x));
        ring.setAttribute('cy', String(seat.y));
        ring.setAttribute('r', '2.6');
        ring.setAttribute('fill', 'none');
        ring.setAttribute('stroke', metal);
        ring.setAttribute('stroke-width', '1.4');
        svg.append(ring);
      }

      const group = document.createElementNS(SVG_NS, 'g');
      const top = inside ? hangs.y - holder.seat * charmH : hangs.y + holder.gap;
      group.setAttribute('transform', `translate(${hangs.x - charmW / 2} ${top}) scale(${scale})`);
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('d', this.#charm.path);
      path.setAttribute('fill', metal);
      path.setAttribute('fill-rule', 'evenodd');
      group.append(path);
      svg.append(group);
    } else {
      const ring = document.createElementNS(SVG_NS, 'circle');
      ring.setAttribute('cx', String(hangs.x));
      ring.setAttribute('cy', String(hangs.y + 2));
      ring.setAttribute('r', '2.6');
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', metal);
      ring.setAttribute('stroke-width', '1.4');
      svg.append(ring);
    }

    // the stones, threaded on the chain either side of the charm
    this.#stones.forEach((value, i) => {
      const { colour } = readStone(value);
      let spot;
      if (holder.chain) {
        // off the keychain's chain: a little link out to the side, the stone at its end
        const { x, top, bottom } = holder.chain;
        const side = i % 2 === 0 ? -1 : 1;
        const y = top + 5 + i * ((bottom - top - 12) / 3);
        spot = { x: x + side * (holder.bead + 3.4), y: y + 3 };
        const hook = document.createElementNS(SVG_NS, 'line');
        hook.setAttribute('x1', String(x));
        hook.setAttribute('y1', String(y));
        hook.setAttribute('x2', String(spot.x));
        hook.setAttribute('y2', String(spot.y));
        hook.setAttribute('stroke', metal);
        hook.setAttribute('stroke-width', '1');
        svg.append(hook);
      } else {
        const side = i % 2 === 0 ? 1 : -1;
        // le pietre partono dopo il ciondolo: nel bracciale il ciondolo occupa il mezzo del cinturino
        const first = charmW && holder.sides ? charmW / 2 + 12 : holder.first;
        spot = along(side * (first + Math.floor(i / 2) * holder.step));
      }

      const bead = document.createElementNS(SVG_NS, 'circle');
      bead.setAttribute('cx', String(spot.x));
      bead.setAttribute('cy', String(spot.y));
      bead.setAttribute('r', String(holder.bead));
      bead.setAttribute('fill', colour);
      bead.setAttribute('stroke', metal);
      bead.setAttribute('stroke-width', '1.4');
      svg.append(bead);
    });

    if (liveHint) liveHint.hidden = this.#stones.length > 0;
  }

  /** Points along the piece, measured out from its middle (the keychain's chain is placed on its own). */
  #along(line, holder) {
    let total = 0;
    try {
      total = line.getTotalLength();
    } catch {
      total = 0;
    }
    if (!total) return (offset) => ({ x: 100 + offset, y: holder.beads });
    const middle = total / 2;
    return (offset) => line.getPointAtLength(Math.min(total, Math.max(0, middle + offset)));
  }

  #confirmStones(skip) {
    if (skip) this.#stones = [];
    const said = this.#stones.length
      ? this.#stones.map((value) => readStone(value).month).join(', ')
      : this.settings.stonesSkip || 'No stones';
    this.#you(said, 5);
    this.#say('');
    this.#goto(this.#catalogue.gift ? 'gift' : 'done');
  }

  // — step 7: the gift box, and into the cart ———————————————————

  /** What the box costs belongs on the button, not in the fine print. */
  #priceTheGift() {
    const { giftYes } = this.refs;
    const gift = this.#catalogue.gift;
    if (!giftYes) return;
    const label = this.settings.giftYes || 'Yes';
    if (!gift?.price) {
      giftYes.textContent = label;
      return;
    }
    const price = this.#money(gift.price);
    // on the smallest phones "Yes, pack it" loses its "Yes," so the button fits next to "No, thanks"
    const bare = label.replace(/^yes,?\s*/i, '');
    const long = document.createElement('span');
    long.className = 'pawbau__gift-long';
    long.textContent = `${label} · ${price}`;
    const short = document.createElement('span');
    short.className = 'pawbau__gift-short';
    short.textContent = `${bare ? bare[0].toUpperCase() + bare.slice(1) : label} · ${price}`;
    giftYes.replaceChildren(long, short);
  }

  async #finish(withGift) {
    const { gift, halo } = this.#catalogue;
    this.#you(withGift ? this.settings.giftYes : this.settings.giftNo, 6);
    this.#hideReplies();
    this.#say(this.settings.adding);
    window.pbEv?.('add_click');

    const variant = this.#variantId();
    if (!variant) return this.#warn(this.settings.cartFailed);

    // cutting the file and sending it up takes a few seconds: give them the bench to watch
    const packing = this.#showPacking(STEPS.indexOf('gift'));

    const { name, metal, kind, key } = this.#fileKey();
    if (!this.#dxf || this.#painted !== metal || this.#cut !== kind) {
      let files = this.#early?.key === key ? await this.#early.files : { url: '', image: '' };
      if (!files.url) files = await this.#prepare(this.#breed, name, metal, kind, this.#halo);
      this.#dxf = files.url;
      this.#picture = files.image;
      this.#painted = metal;
      this.#cut = kind;
    }

    if (this.#adding) return this.#backToOrder(withGift, packing, name, kind);

    // One stamp ties the piece and its add-ons together, the way the cart reads them back.
    const stamp = String(Date.now());
    const stoneNames = {};
    this.#stones.forEach((value, i) => {
      stoneNames[`Birthstone ${i + 1}`] = value;
    });

    const items = [
      {
        id: variant,
        quantity: 1,
        properties: {
          Breed: this.#label(this.#breed),
          // without a name the line says only the breed; the engine and the order sheet read _no_name and the file
          ...(name ? { Name: name } : {}),
          ...stoneNames,
          // the box is a hidden line, so it is said here or the buyer never sees it
          ...(withGift ? { 'Gift box': this.settings.giftYes || 'Yes' } : {}),
          _breed: this.#breed,
          _piece: kind,
          ...(name ? {} : { _no_name: '1' }),
          ...(this.#halo ? { _halo: '1' } : {}),
          _created_at: stamp,
          ...(this.#dxf ? { _dxf: this.#dxf } : {}),
          ...(this.#picture ? { _preview: this.#picture } : {}),
          _customizer: 'chat',
        },
      },
    ];
    for (const value of this.#stones) {
      const stone = this.#ware.stones?.variants.find((v) => v.name === value);
      // the stone stays its own line for production, but hangs off the piece in the cart
      if (stone) {
        items.push({
          id: stone.id,
          quantity: 1,
          properties: { _parent: this.#ware.handle || this.#ware.key, _created_at: stamp },
        });
      }
    }
    if (this.#halo && halo?.id) {
      items.push({ id: halo.id, quantity: 1, properties: { _parent: this.#ware.handle || this.#ware.key, _created_at: stamp } });
    }
    if (withGift && gift?.id) {
      items.push({ id: gift.id, quantity: 1, properties: { _parent: this.#ware.handle || this.#ware.key, _created_at: stamp } });
    }

    try {
      const res = await fetch(this.settings.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const added = await res.json();
      // the order says which customizer made it: that is how the A/B test is read
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { Customizer: 'chat' } }),
      }).catch(() => {});

      // reworking a piece: the lines it had before make way for the new ones
      if (this.#editing >= 0) await this.#dropLines(this.#made[this.#editing]?.lines || []);

      const entry = {
        breed: this.#breed,
        name,
        charm: this.#charm,
        dxf: this.#dxf,
        picture: this.#picture,
        ware: this.#ware,
        picked: { ...this.#picked },
        stones: [...this.#stones],
        halo: this.#halo,
        gift: withGift,
        lines: (added.items || []).map((line) => line.key).filter(Boolean),
      };
      window.pbEv?.('added');
      if (this.#editing >= 0) this.#made[this.#editing] = entry;
      else this.#made.push(entry);
      this.#editing = -1;
      this.#trackCart(entry, variant);

      packing.stop();
      this.#drawRecap();
      this.#say('');
      this.#goto('done');
    } catch {
      packing.stop();
      this.#warn(this.settings.cartFailed);
      this.#showReply('gift');
    }
  }

  /** What Mia says first when she was opened from the Track page. */
  #addingLine() {
    const a = this.#adding;
    const text = (a.box ? this.settings.addingBox : this.settings.addingLater) || '';
    return text.replace('[order]', `#${a.order}`).replace('[price]', this.settings.bundlePrice || '');
  }

  /**
   * The piece for an order already paid: it waits in this tab (sessionStorage) for the Track page, where it is
   * bought with the rest. The page asks the order sheet to make the invoice; the design file is made there again.
   */
  #backToOrder(withGift, packing, name, kind) {
    const key = `pb_add:${this.#adding.order}`;
    let basket = [];
    try {
      basket = JSON.parse(sessionStorage.getItem(key) || '[]');
    } catch {
      basket = [];
    }
    basket.push({
      pet: { name, breed: this.#breed, label: this.#label(this.#breed), halo: this.#halo },
      piece: kind,
      finish: this.#picked.Finish || '',
      cord: this.#picked.Cord || '',
      gift: withGift,
      image: this.#picture,
    });
    try {
      sessionStorage.setItem(key, JSON.stringify(basket.slice(-4)));
    } catch {
      packing.stop();
      return this.#warn(this.settings.cartFailed);
    }
    window.pbEv?.('added');
    const page = this.settings.trackUrl || '/pages/track-your-order';
    // #mia, not #add: back from the chat is not a click on the email button
    location.href = `${page}?order=${encodeURIComponent(this.#adding.order)}&t=${encodeURIComponent(this.#adding.t)}#mia`;
  }

  /** The wait, made worth watching: their own charm drawn again while she says what she is doing. */
  #showPacking(at) {
    const bubble = document.createElement('div');
    bubble.className = 'pawbau__bubble pawbau__bubble--mia pawbau__packing';
    bubble.setAttribute('aria-label', this.settings.adding || 'Putting it in your cart');

    const line = document.createElement('p');
    line.className = 'pawbau__packing-line';
    const bar = document.createElement('span');
    bar.className = 'pawbau__packing-bar';
    bubble.append(line, bar);
    const row = this.#fromMia(bubble, at);

    if (this.#charm) {
      const svg = this.#svg(this.#charm.viewBox, this.#charm.path, this.#breed);
      bubble.prepend(svg);
      for (const path of svg.querySelectorAll('path')) {
        const length = path.getTotalLength();
        path.style.strokeDasharray = String(length);
        path.style.setProperty('--len', String(length));
      }
    }

    const steps = (this.settings.packingSteps || this.settings.adding || '')
      .split('\n')
      .map((step) => step.trim())
      .filter(Boolean);
    line.textContent = steps[0] || '';
    let step = 0;
    const walk = window.setInterval(() => {
      step = Math.min(step + 1, steps.length - 1);
      line.textContent = steps[step] || '';
    }, PACKING_STEP_MS);

    this.#scroll();
    return {
      stop() {
        window.clearInterval(walk);
        row.remove();
      },
    };
  }

  /** Takes lines out of the cart, one call each: the cart only changes one line at a time. */
  async #dropLines(keys) {
    for (const key of keys) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      } catch {
        // a line that will not go is left in the cart rather than blocking the buyer
      }
    }
  }

  // — the recap strip ———————————————————————————————————————————

  /** One card per piece made in this session, each one a way back into it. */
  #drawRecap() {
    const { recap } = this.refs;
    if (!recap) return;
    recap.replaceChildren();
    recap.hidden = this.#made.length === 0;

    this.#made.forEach((entry, index) => {
      const card = document.createElement('div');
      card.className = 'pawbau__made';
      if (index === this.#editing) card.classList.add('pawbau__made--editing');

      const thumb = document.createElement('figure');
      if (entry.charm) {
        const shape = this.#svg(entry.charm.viewBox, entry.charm.path, entry.breed);
        shape.style.color = DRAWN[entry.picked?.Finish] || '';
        thumb.append(shape);
      }
      card.append(thumb);

      const body = document.createElement('div');
      body.className = 'pawbau__made-body';
      const who = document.createElement('b');
      who.textContent = entry.name || this.#label(entry.breed);
      const what = document.createElement('span');
      what.textContent = [entry.ware?.title, ...Object.values(entry.picked)].filter(Boolean).join(' · ');
      const extras = document.createElement('small');
      const bits = entry.stones.map((value) => readStone(value).month);
      if (entry.halo) bits.push(this.#catalogue.halo?.title || 'Halo');
      if (entry.gift) bits.push(this.#catalogue.gift?.title || 'Gift box');
      extras.textContent = bits.join(' · ');
      body.append(who, what, extras);
      card.append(body);

      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'pawbau__edit';
      edit.textContent = this.settings.editLabel || 'Edit';
      edit.addEventListener('click', () => this.#rework(index));
      card.append(edit);

      recap.append(card);
    });

    // Safari on iPhone does not always paint cards added to a side-scrolling shelf that is already
    // on screen: measuring it and bringing it back to the start makes it draw them now
    if (!recap.hidden) {
      void recap.offsetWidth;
      recap.scrollLeft = 0;
      requestAnimationFrame(() => {
        recap.style.transform = 'translateZ(0) scale(1)';
        requestAnimationFrame(() => recap.style.removeProperty('transform'));
      });
    }
  }

  /** Picking a piece up again: the conversation is rebuilt around it, from the charm onwards. */
  #rework(index) {
    const entry = this.#made[index];
    if (!entry) return;

    this.#turn += 1;
    this.#editing = index;
    this.#breed = entry.breed;
    this.#charm = entry.charm;
    this.#dxf = entry.dxf;
    this.#picture = entry.picture || '';
    this.#halo = Boolean(entry.halo);
    if (this.refs.haloBox) this.refs.haloBox.checked = this.#halo;
    this.#drawn = true;
    this.#ware = entry.ware;
    this.#picked = { ...entry.picked };
    this.#stones = [...entry.stones];
    this.#species = this.#speciesOf(entry.breed);
    this.refs.breedInput.value = this.#label(entry.breed);
    // the space stands for the charm without a name, as when they first asked for it
    this.refs.nameInput.value = entry.name || ' ';

    // the thread starts again on what was already decided, so every answer keeps its Change
    this.refs.thread.replaceChildren();
    this.#you(this.#speciesLabel(this.#species), 0);
    this.#you(this.#label(entry.breed), 1);
    this.#you(entry.name || this.settings.noName, 2);
    if (entry.charm) this.#showCharm(2, entry.breed);

    this.#drawRecap();
    this.#goto('product');
  }

  /** The variant that matches every option the buyer chose. */
  #variantId() {
    if (!this.#ware) return null;
    const match = this.#ware.variants.find((variant) =>
      this.#ware.options.every((name, i) => variant.options[i] === this.#picked[name])
    );
    return match ? match.id : null;
  }

  /** The cutting file and its picture, made before the order exists so both travel with the line.
   *  The piece decides where the rings are welded: at the top for a necklace or a keychain, at the sides
   *  for a bracelet. None of them show up in the picture — that is the charm, not how it hangs. */
  async #prepare(breed, name, colour = '', piece = 'necklace', halo = false) {
    try {
      const tint = colour.replace('#', '');
      const url = `${this.engine}/prepare?breed=${encodeURIComponent(breed)}&name=${encodeURIComponent(name)}&colour=${encodeURIComponent(tint)}&piece=${encodeURIComponent(piece)}${halo ? '&halo=1' : ''}`;
      const res = await fetch(url);
      if (!res.ok) return { url: '', image: '' };
      const data = await res.json();
      return { url: data.url || '', image: data.image || '' };
    } catch {
      // The order still carries breed and name: the service makes the file when the order lands.
      return { url: '', image: '' };
    }
  }

  /** What the cutting file depends on: breed, name, finish, piece and halo. Stones and gift box don't. */
  #fileKey() {
    const name = this.#petName();
    const metal = DRAWN[this.#picked.Finish] || '#1C1C1C';
    const kind = this.#ware?.key || 'necklace';
    return { name, metal, kind, key: [this.#breed, name, metal, kind, this.#halo ? 1 : 0].join('|') };
  }

  /** Start the file as soon as everything it depends on is chosen, while they still pick stones and box. */
  #prepareEarly() {
    const { name, metal, kind, key } = this.#fileKey();
    // the charm drawn is what gets cut, with its name or without one
    if (!this.#breed || !this.#drawn || this.#early?.key === key) return;
    this.#early = { key, files: this.#prepare(this.#breed, name, metal, kind, this.#halo) };
  }

  // — Klaviyo ———————————————————————————————————————————————————

  /** Klaviyo's onsite script, when its embed is on: it sends the event once it knows who is designing. */
  #track(metric, properties) {
    try {
      const klaviyo = (window.klaviyo = window.klaviyo || []);
      klaviyo.push(['track', metric, properties]);
    } catch {
      // without Klaviyo the conversation goes on the same
    }
  }

  /** The address that brings this pet back into the conversation, for an email to link to. */
  #resumeLink(name, breed) {
    const url = new URL(location.pathname, location.origin);
    // the charm without a name comes back the way it was asked for
    url.searchParams.set('pet', name || 'blank');
    url.searchParams.set('breed', breed);
    if (this.#halo) url.searchParams.set('halo', '1');
    url.hash = 'design';
    return url.href;
  }

  /** Shopify writes its images as //cdn…: an email needs the whole address. */
  #absolute(src) {
    return src ? new URL(src, location.href).href : '';
  }

  /** The charm is drawn: told once, so the reminder can say the pet's name. The drawing itself never travels. */
  #trackDrawing(name, breed) {
    const key = `${breed}|${name}|${this.#halo ? 1 : 0}`;
    if (this.#tracked.has(key)) return;
    this.#tracked.add(key);
    this.#track('Preview Created', {
      PetName: name,
      Breed: this.#label(breed),
      BreedKey: breed,
      Species: this.#species || this.#speciesOf(breed),
      Halo: this.#halo,
      ImageURL: this.#absolute(this.#catalogue.products?.[0]?.image),
      ResumeURL: this.#resumeLink(name, breed),
      PageURL: location.origin + location.pathname,
    });
  }

  /** A piece went in the cart: what it is and whose name it carries, for the cart reminder. */
  #trackCart(entry, variantId) {
    const ware = entry.ware || {};
    const variant = ware.variants?.find((v) => v.id === variantId);
    const pieceNames = { necklace: 'necklace', bracelet: 'cord bracelet', keychain: 'keychain' };
    this.#track('Added Piece to Cart', {
      PetName: entry.name,
      Breed: this.#label(entry.breed),
      BreedKey: entry.breed,
      Piece: ware.key || '',
      PieceName: pieceNames[ware.key] || ware.title || '',
      ProductName: ware.title || '',
      Options: Object.values(entry.picked).filter(Boolean).join(' / '),
      Finish: entry.picked.Finish || '',
      Birthstones: entry.stones,
      GiftBox: entry.gift,
      Halo: entry.halo,
      ImageURL: this.#absolute(ware.image),
      CartURL: new URL(this.settings.cartUrl || '/cart', location.origin).href,
      ResumeURL: this.#resumeLink(entry.name, entry.breed),
      PiecesInCart: this.#made.length,
      $value: Number(variant?.price ?? ware.price ?? 0) / 100,
    });
  }

  // — small helpers —————————————————————————————————————————————

  #say(message) {
    const { status } = this.refs;
    status.textContent = message || '';
    status.classList.remove('pawbau__status--warn');
  }

  #warn(message) {
    const { status } = this.refs;
    status.textContent = message || '';
    status.classList.toggle('pawbau__status--warn', Boolean(message));
  }
}

customElements.define('pawbau-configurator-component', PawbauConfigurator);
